import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink, History, Pause, Play, Plus, Search, Trash2 } from "lucide-react";
import {
  type AggSource,
  type ChannelDetail,
  type SourcePreview,
  countSourceLines,
  isInviteLink,
  sourceLabel,
  useAddSources,
  usePreviewSource,
  useRemoveSource,
  useSetSourceStatus,
  useSourceByAccount,
} from "@/lib/aggregator";
import { formatAccountNo } from "@/lib/accounts";
import { formatNumber, formatRelative } from "@/lib/format";
import { pushToast } from "@/components/Toaster";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { FillDialog } from "./FillPanel";
import { ICON_BUTTON, TEXTAREA, errorText } from "./helpers";
import { MediaStrip, RichTextView, Section } from "./parts";

/**
 * The channels this channel is filled from. An open one is read from its
 * public page — no account of the fleet goes near it. A closed channel or a
 * group, given by an invite link, is read by an account with the parser's
 * role, which joins by itself: the row says which account and how far it got.
 */
export function SourcesTab({
  channel,
  canEdit,
  text,
  setText,
}: {
  channel: ChannelDetail;
  canEdit: boolean;
  /** The list being pasted in — kept by the card, so that a look at another
   *  tab does not throw it away. */
  text: string;
  setText: (text: string) => void;
}) {
  const { t } = useTranslation();
  const add = useAddSources();
  const preview = usePreviewSource();
  const remove = useRemoveSource();
  const setStatus = useSetSourceStatus();
  const byAccount = useSourceByAccount();
  const [shown, setShown] = useState<SourcePreview | null>(null);
  // The sources just added — offered for a fill; and the dialog's own.
  const [added, setAdded] = useState<string[]>([]);
  const [fillFrom, setFillFrom] = useState<string[] | null>(null);
  const filling = channel.fill?.status === "running";
  // Offered once the card lists them — and not for one removed since.
  const addedHere = added.filter((id) => channel.source_list.some((s) => s.id === id));
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [removing, setRemoving] = useState<AggSource | null>(null);

  const named = countSourceLines(text);

  const look = async () => {
    setError(null);
    setNote(null);
    setShown(null);
    const first = text.trim().split("\n")[0];
    if (isInviteLink(first)) {
      // A closed chat has no page to show; nothing is asked of anybody.
      setNote(t("aggregator.sources.closed_preview"));
      return;
    }
    try {
      setShown(await preview.mutateAsync(first));
    } catch (err) {
      setError(errorText(err, t("aggregator.action_error")));
    }
  };

  const submit = async () => {
    setError(null);
    setNote(null);
    try {
      const out = await add.mutateAsync({ id: channel.id, text });
      setAdded(out.added_ids);
      const parts = [t("aggregator.sources.added", { count: out.added })];
      if (out.already > 0) parts.push(t("aggregator.sources.added_already", { count: out.already }));
      if (out.via_account > 0) {
        parts.push(t("aggregator.sources.added_via_account", { count: out.via_account }));
      }
      if (out.skipped > 0) parts.push(t("aggregator.sources.added_skipped", { count: out.skipped }));
      const troubles = [
        ...out.troubled.map(
          (item) => `@${item.username}: ${t(`aggregator.problem.${item.problem}`, item.problem)}`,
        ),
        ...out.unsupported.map((line) => `${line}: ${t("aggregator.sources.unsupported")}`),
      ];
      pushToast({
        kind: out.added > 0 && troubles.length === 0 ? "success" : "info",
        text: parts.join(" · "),
        detail: troubles.length > 0 ? troubles.join("\n") : undefined,
      });
      setText("");
      setShown(null);
    } catch (err) {
      setError(errorText(err, t("aggregator.action_error")));
    }
  };

  return (
    <div className="space-y-6">
      {channel.source_list.length === 0 ? (
        <EmptyState
          icon={Search}
          title={t("aggregator.sources.empty_title")}
          description={t("aggregator.sources.empty_hint")}
        />
      ) : (
        <ul className="space-y-2">
          {channel.source_list.map((source) => (
            <li
              key={source.id}
              className="space-y-2 rounded-xl border border-border bg-bg-primary px-4 py-3"
            >
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <a
                      href={source.link}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex min-w-0 items-center gap-1 font-semibold text-fg-primary hover:underline"
                    >
                      <span className="truncate">{source.title ?? sourceName(source, t)}</span>
                      <ExternalLink size={13} strokeWidth={2} aria-hidden="true" className="shrink-0 text-fg-muted" />
                    </a>
                    {source.problem ? (
                      <Badge tone="danger">
                        {t(`aggregator.problem.${source.problem}`, source.problem)}
                      </Badge>
                    ) : source.status === "paused" ? (
                      <Badge tone="warning">{t("aggregator.sources.paused")}</Badge>
                    ) : source.via === "account" && source.join_state !== "joined" ? (
                      <Badge tone="info">
                        {t(
                          source.join_state === "requested"
                            ? "aggregator.sources.requested"
                            : "aggregator.sources.joining",
                        )}
                      </Badge>
                    ) : (
                      <Badge tone="success" dot>
                        {t("aggregator.sources.reading")}
                      </Badge>
                    )}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-fg-muted">
                    {sourceName(source, t)}
                    {source.is_group && ` · ${t("aggregator.sources.group")}`}
                    {source.via === "account" &&
                      ` · ${
                        source.account_seq_no !== null
                          ? t("aggregator.sources.via_account", {
                              account: formatAccountNo(source.account_seq_no),
                            })
                          : t("aggregator.sources.no_account")
                      }`}
                    {source.subscribers !== null &&
                      ` · ${t("aggregator.sources.subscribers", { n: formatNumber(source.subscribers) })}`}
                    {source.last_checked_at &&
                      ` · ${t("aggregator.sources.checked", { when: formatRelative(source.last_checked_at) })}`}
                  </div>
                </div>
                <div className="text-right text-xs text-fg-muted">
                  <div className="font-mono text-sm tabular-nums text-fg-primary">
                    {source.published_7d}/{source.taken_7d}
                  </div>
                  <div>{t("aggregator.sources.week")}</div>
                </div>
                {canEdit && (
                  <div className="flex items-center">
                    <button
                      type="button"
                      className={ICON_BUTTON}
                      disabled={filling}
                      aria-label={t("aggregator.fill.from_source")}
                      title={t(
                        filling ? "aggregator.fill.running_already" : "aggregator.fill.from_source",
                      )}
                      onClick={() => setFillFrom([source.id])}
                    >
                      <History size={16} strokeWidth={2} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className={ICON_BUTTON}
                      aria-label={t(
                        source.status === "active" ? "aggregator.sources.pause" : "aggregator.sources.resume",
                      )}
                      title={t(
                        source.status === "active" ? "aggregator.sources.pause" : "aggregator.sources.resume",
                      )}
                      onClick={() =>
                        setStatus.mutate(
                          {
                            id: channel.id,
                            source_id: source.id,
                            status: source.status === "active" ? "paused" : "active",
                          },
                          {
                            onError: (err) =>
                              pushToast({
                                kind: "error",
                                text: errorText(err, t("aggregator.action_error")),
                              }),
                          },
                        )
                      }
                    >
                      {source.status === "active" ? (
                        <Pause size={16} strokeWidth={2} aria-hidden="true" />
                      ) : (
                        <Play size={16} strokeWidth={2} aria-hidden="true" />
                      )}
                    </button>
                    <button
                      type="button"
                      className={ICON_BUTTON}
                      aria-label={t("aggregator.sources.remove")}
                      title={t("aggregator.sources.remove")}
                      onClick={() => setRemoving(source)}
                    >
                      <Trash2 size={16} strokeWidth={2} aria-hidden="true" />
                    </button>
                  </div>
                )}
              </div>
              {source.problem_detail && (
                <p className="text-xs text-danger">{source.problem_detail}</p>
              )}
              {canEdit && source.problem === "no_preview" && source.via === "page" && (
                // No account is sent after a name whose page shows nothing unless
                // a person says so: the page of a channel that reads well goes
                // blank now and then.
                <Button
                  size="sm"
                  variant="outline"
                  loading={byAccount.isPending && byAccount.variables?.source_id === source.id}
                  onClick={() =>
                    byAccount.mutate(
                      { id: channel.id, source_id: source.id },
                      {
                        onSuccess: () =>
                          pushToast({
                            kind: "success",
                            text: t("aggregator.sources.by_account_done"),
                          }),
                        onError: (err) =>
                          pushToast({
                            kind: "error",
                            text: errorText(err, t("aggregator.action_error")),
                          }),
                      },
                    )
                  }
                >
                  {t("aggregator.sources.by_account")}
                </Button>
              )}
              {!source.problem && source.via === "account" && source.join_state !== "joined" && (
                <p className="text-xs leading-relaxed text-fg-muted">
                  {t(
                    source.join_state === "requested"
                      ? "aggregator.sources.requested_hint"
                      : "aggregator.sources.joining_hint",
                  )}
                </p>
              )}
              {source.shared_with > 0 && (
                <p className="text-xs text-fg-muted">
                  {t("aggregator.sources.shared", { count: source.shared_with })}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit && addedHere.length > 0 && !filling && (
        <Alert
          tone="info"
          action={
            <Button
              size="sm"
              onClick={() => setFillFrom(addedHere)}
              iconLeft={<History size={14} strokeWidth={2} aria-hidden="true" />}
            >
              {t("aggregator.fill.after_add_button")}
            </Button>
          }
        >
          {t("aggregator.fill.after_add")}
        </Alert>
      )}
      {channel.source_list.some((source) => source.via === "account") && (
        // The account's proxy carries the photos and videos: one paid by the
        // gigabyte is money the operator should know about.
        <Alert tone="info">{t("aggregator.sources.proxy_traffic")}</Alert>
      )}

      {canEdit && (
        <Section title={t("aggregator.sources.add_title")} hint={t("aggregator.sources.add_hint")}>
          <textarea
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setError(null);
              setNote(null);
            }}
            rows={3}
            placeholder={"@channel\nhttps://t.me/another_channel\nhttps://t.me/+AbCdEfGh…"}
            className={TEXTAREA}
            aria-label={t("aggregator.sources.add_title")}
          />
          <div className="flex flex-wrap items-end justify-end gap-3">
            <Button
              variant="outline"
              onClick={look}
              loading={preview.isPending}
              disabled={named === 0 || preview.isPending}
              iconLeft={<Search size={15} strokeWidth={2} aria-hidden="true" />}
            >
              {t("aggregator.sources.preview")}
            </Button>
            <Button
              onClick={submit}
              loading={add.isPending}
              disabled={named === 0 || add.isPending}
              iconLeft={<Plus size={16} strokeWidth={2} aria-hidden="true" />}
            >
              {t("aggregator.sources.add", { count: named })}
            </Button>
          </div>
          {error && <Alert tone="danger">{error}</Alert>}
          {note && <Alert tone="info">{note}</Alert>}
          {shown && <Preview shown={shown} />}
        </Section>
      )}

      <FillDialog
        channel={channel}
        open={fillFrom !== null}
        onOpenChange={(o) => {
          if (o) return;
          setFillFrom(null);
          setAdded([]);
        }}
        only={fillFrom}
      />
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(o) => !o && setRemoving(null)}
        tone="danger"
        title={t("aggregator.sources.remove_confirm", {
          name: removing ? sourceLabel(removing, t("aggregator.sources.closed_name")) : "",
        })}
        description={t("aggregator.sources.remove_hint")}
        confirmText={t("aggregator.sources.remove")}
        cancelText={t("common.cancel")}
        onConfirm={async () => {
          if (!removing) return;
          try {
            await remove.mutateAsync({ id: channel.id, source_id: removing.id });
          } catch (err) {
            pushToast({ kind: "error", text: errorText(err, t("aggregator.action_error")) });
          }
        }}
      />
    </div>
  );
}

/** A source by the name a person knows it by: its @name — or, for one given
 *  as an invite link (kept as "+<hash>"), what it is. */
function sourceName(source: AggSource, t: (key: string) => string): string {
  return source.username.startsWith("+")
    ? t("aggregator.sources.closed")
    : `@${source.username}`;
}

/** What a channel's page gave, before the channel is added. */
function Preview({ shown }: { shown: SourcePreview }) {
  const { t } = useTranslation();
  if (!shown.has_preview) {
    return (
      <Alert tone="warning" title={t("aggregator.sources.no_preview_title", { name: shown.username })}>
        {t("aggregator.sources.no_preview_text")}
      </Alert>
    );
  }
  return (
    <div className="space-y-3 rounded-xl border border-border bg-bg-primary px-4 py-3">
      <div className="text-sm">
        <span className="font-semibold text-fg-primary">{shown.title ?? `@${shown.username}`}</span>
        {shown.subscribers !== null && (
          <span className="text-fg-muted">
            {" · "}
            {t("aggregator.sources.subscribers", { n: formatNumber(shown.subscribers) })}
          </span>
        )}
      </div>
      <p className="text-xs text-fg-muted">{t("aggregator.sources.preview_hint")}</p>
      {shown.posts.length === 0 && (
        <p className="text-sm text-fg-muted">{t("aggregator.sources.preview_empty")}</p>
      )}
      {shown.posts.map((post) => (
        <div key={post.message_id} className="space-y-2 border-t border-border pt-3">
          <div className="flex flex-wrap items-center gap-2 text-xs text-fg-muted">
            {post.posted_at && <span>{formatRelative(post.posted_at)}</span>}
            {post.views !== null && (
              <span>{t("aggregator.post.views", { n: formatNumber(post.views) })}</span>
            )}
            {post.is_reply && <Badge>{t("aggregator.post.is_reply")}</Badge>}
            {post.forwarded_from && (
              <Badge>{t("aggregator.post.forwarded", { from: post.forwarded_from })}</Badge>
            )}
          </div>
          <MediaStrip media={post.media} missing={post.media_missing} t={t} />
          <RichTextView text={post.text} clamp />
        </div>
      ))}
    </div>
  );
}
